import axios from 'axios';

// Base URL for FileShare microservice (through auth server gateway)
// Use environment variable with fallback to localhost for local development
const getBaseUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/';
  // Ensure backendUrl ends with / and remove trailing slash if present
  const baseUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
  return `${baseUrl}/fileshare/api`;
};

const FILE_SHARE_BASE_URL = getBaseUrl();

// Create axios instance with default config
const fileShareApi = axios.create({
  baseURL: FILE_SHARE_BASE_URL,
  timeout: 60000, // Increased default timeout to 60 seconds
});

// Add request interceptor to include auth headers
fileShareApi.interceptors.request.use(
  (config) => {
    // Add JWT token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // FileShare backend requires X-Tenant-Id; tenant schema (db_name) is resolved
    // server-side from tenantId via TenantContextFilter / TenantIdentifierResolver.
    // User identity comes from Authorization Bearer (JWT) — do not send X-User-Id from localStorage.
    const tenantId = localStorage.getItem('tenantId');

    if (tenantId) {
      config.headers['X-Tenant-Id'] = tenantId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
fileShareApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('FileShare API Error:', error);
    return Promise.reject(error);
  }
);

// Tenant-wide comment notification emails (FileShare settings)
export const commentNotificationSettingsApi = {
  get: () => fileShareApi.get('/settings/comment-notifications'),
  save: (notificationEmails) =>
    fileShareApi.put('/settings/comment-notifications', { notificationEmails }),
};

// Project Spaces API
export const projectSpacesApi = {
  // Get all project spaces for current tenant (active by default)
  getAll: (params = {}) =>
    fileShareApi.get('/projects', {
      params: { archived: params.archived === true },
    }),
  
  // Get project space by ID
  getById: (projectId) => fileShareApi.get(`/projects/${projectId}`),
  
  // Create new project space
  create: (projectData) => fileShareApi.post('/projects', projectData),
  
  // Update project space
  update: (projectId, projectData) => fileShareApi.put(`/projects/${projectId}`, projectData),
  
  // Delete project space
  delete: (projectId) => fileShareApi.delete(`/projects/${projectId}`),

  // Archive project (hide from main list; data kept)
  archive: (projectId) => fileShareApi.post(`/projects/${projectId}/archive`),

  // Restore archived project to main list
  unarchive: (projectId) => fileShareApi.post(`/projects/${projectId}/unarchive`),
  
  // Get project spaces by user
  getByUser: (userId, params = {}) =>
    fileShareApi.get(`/projects/user/${userId}`, {
      params: { archived: params.archived === true },
    }),

  // Get storage + forge connectors assigned to a project
  getConnectors: (projectId) => fileShareApi.get(`/projects/${projectId}/connectors`),
};

// Folders API
export const foldersApi = {
  // Get all folders in project
  getByProject: (projectId) => fileShareApi.get(`/folders/project/${projectId}`),
  
  // Get root folders in project
  getRootByProject: (projectId) => fileShareApi.get(`/folders/project/${projectId}/root`),
  
  // Get folder tree for project
  getTreeByProject: (projectId) => fileShareApi.get(`/folders/project/${projectId}/tree`),
  
  // Get folder by ID
  getById: (folderId) => fileShareApi.get(`/folders/${folderId}`),
  
  // Create new folder
  create: (folderData) => fileShareApi.post('/folders', folderData),
  
  // Update folder
  update: (folderId, folderData) => fileShareApi.put(`/folders/${folderId}`, folderData),
  
  // Move folder
  move: (folderId, moveData) => fileShareApi.put(`/folders/${folderId}/move`, moveData),
  
  // Delete folder
  delete: (folderId) => fileShareApi.delete(`/folders/${folderId}`),
  
  // Get subfolders
  getSubFolders: (folderId) => fileShareApi.get(`/folders/${folderId}/subfolders`),
};

// Files API
export const filesApi = {
  // Upload file
  upload: (formData) => fileShareApi.post('/files/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  
  // Get file by ID
  getById: (fileId) => fileShareApi.get(`/files/${fileId}`),

  // Get comments for a file
  getComments: (fileId) => fileShareApi.get(`/files/${fileId}/comments`),
  
  // Get files in folder
  getByFolder: (folderId) => fileShareApi.get(`/files/folder/${folderId}`),
  
  // Get file versions
  getVersions: (fileId) => fileShareApi.get(`/files/${fileId}/versions`),
  
  // Generate download URL
  getDownloadUrl: (fileId) => fileShareApi.get(`/files/${fileId}/download`),
  
  // Generate download URL for specific version
  getDownloadUrlForVersion: (fileId, versionNumber) => 
    fileShareApi.get(`/files/${fileId}/versions/${versionNumber}/download`),
  
  // Get Forge viewer URN
  getViewerUrn: (fileId) => fileShareApi.get(`/files/${fileId}/viewer-urn`),
  
  // Get translation status (with longer timeout for Forge API calls)
  getTranslationStatus: (fileId) => fileShareApi.get(`/files/${fileId}/translation-status`, {
    timeout: 60000, // 60 seconds timeout for translation status checks
  }),
  
  // Delete file
  delete: (fileId) => fileShareApi.delete(`/files/${fileId}`),
  
  // Sync files from storage bucket (long timeout: listing + DB writes for many/large files)
  syncProjectFolders: (projectId) =>
    fileShareApi.post(`/storage/project/${projectId}/sync-folders`, null, {
      timeout: 300000, // 5 minutes for projects with many or large (e.g. 600MB+) files
    }),
  
  syncFromBucket: (projectId, folderId = null, s3Prefix = null) => {
    const params = new URLSearchParams();
    params.append('projectId', projectId);
    if (folderId) params.append('folderId', folderId);
    if (s3Prefix) params.append('s3Prefix', s3Prefix);
    return fileShareApi.post(`/files/bucket/sync?${params.toString()}`);
  },
};

// Autodesk Forge API
export const forgeApi = {
  // Get Forge access token
  getAccessToken: () => fileShareApi.get('/forge/token'),

  // Get Forge URN for a file
  getForgeUrn: (fileId) => fileShareApi.get(`/forge/files/${fileId}/urn`),

  // Check translation status
  getTranslationStatus: (fileId) => fileShareApi.get(`/forge/files/${fileId}/status`),

  // Upload file to Forge (long timeout: large files can take 10+ minutes to upload)
  // options: { overageApproved?, forceRetranslate? }
  uploadToForge: (fileId, options = {}) => {
    const body = {};
    if (options.overageApproved) {
      body.overageApproved = options.overageApproved;
    }
    if (options.forceRetranslate) {
      body.forceRetranslate = true;
    }
    return fileShareApi.post(`/forge/files/${fileId}/upload`, body, {
      timeout: 600000, // 10 minutes - backend upload is synchronous and can be slow for large files
    });
  },

  // Get viewer configuration
  getViewerConfig: (fileId) => fileShareApi.get(`/forge/files/${fileId}/viewer-config`),
};

// File Share monthly usage and admin-configured limits
export const usageApi = {
  getCurrent: () => fileShareApi.get('/usage/current'),
  getAdmin: () => fileShareApi.get('/usage/admin'),
  updateLimits: (tenantId, limits) =>
    fileShareApi.put(`/usage/admin/${tenantId}/limits`, limits),
};

// Tenants API
export const tenantsApi = {
  // Get all tenants
  getAll: () => fileShareApi.get('/tenants'),
  
  // Get tenant by ID
  getById: (tenantId) => fileShareApi.get(`/tenants/${tenantId}`),
  
  // Create new tenant
  create: (tenantData) => fileShareApi.post('/tenants', tenantData),
  
  // Update tenant
  update: (tenantId, tenantData) => fileShareApi.put(`/tenants/${tenantId}`, tenantData),
  
  // Delete tenant
  delete: (tenantId) => fileShareApi.delete(`/tenants/${tenantId}`),
};

// Storage API - need to use different base URL
const getStorageBaseUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/';
  // Ensure backendUrl ends with / and remove trailing slash if present
  const baseUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
  return `${baseUrl}/fileshare/api/storage`;
};

const storageApiInstance = axios.create({
  baseURL: getStorageBaseUrl(),
  timeout: 60000, // Increased to 60 seconds to match fileShareApi
});

// Add request interceptor for storage API (tenant schema resolved server-side from X-Tenant-Id)
storageApiInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    const tenantId = localStorage.getItem('tenantId');
    if (tenantId) {
      config.headers['X-Tenant-Id'] = tenantId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const storageApi = {
  // List files from storage for a project
  listProjectStorageFiles: (projectId, prefix = '') => 
    storageApiInstance.get(`/project/${projectId}/list`, { params: { prefix } }),
  
  // Get project storage configuration
  getProjectStorageConfig: (projectId) => 
    storageApiInstance.get(`/project/${projectId}/config`),
  
  // Configure storage for a project
  configureProjectStorage: (projectId, storageData) => 
    storageApiInstance.post(`/project/${projectId}/configure`, storageData),
  
  // Test project storage connection
  // Test project storage connection. Pass rootFolderPath to verify a path that is
  // not saved yet (the saved path can only be set once).
  testProjectStorageConnection: (projectId, params = {}) =>
    storageApiInstance.get(`/project/${projectId}/test`, { params }),

  // Get unified file listing for a project (database + storage files)
  getUnifiedProjectFiles: (projectId, prefix = '') =>
    storageApiInstance.get(`/project/${projectId}/unified-files`, { params: { prefix } }),

  // Get unified file listing for a folder (database + storage files)
  getUnifiedFolderFiles: (projectId, folderId, prefix = '') =>
    storageApiInstance.get(`/project/${projectId}/folder/${folderId}/unified-files`, { params: { prefix } }),
};

// Public Share API
export const publicShareApi = {
  // Generate a public share link for a file
  generateShareLink: (fileId, expiryDuration, expiresAt, allowDownload, notificationEmails) =>
    fileShareApi.post('/public-share/generate', {
      fileId,
      expiryDuration, // e.g., "1h", "24h", "7d", "30d"
      expiresAt, // ISO datetime string for custom expiry
      allowDownload,
      notificationEmails, // comma-separated; notified on public comments
    }),

  // All public shares for the current tenant (Share Links screen)
  getAllShares: () => fileShareApi.get('/public-share'),

  // Get all public shares for a file
  getSharesForFile: (fileId) =>
    fileShareApi.get(`/public-share/file/${fileId}`),

  // Get all public shares in a project
  getSharesForProject: (projectId) =>
    fileShareApi.get(`/public-share/project/${projectId}`),

  // Revoke a public share link
  revokeShareLink: (shareId) =>
    fileShareApi.delete(`/public-share/${shareId}`),

  // Get access logs for a share
  getAccessLogs: (shareId) =>
    fileShareApi.get(`/public-share/${shareId}/logs`),
};

// Public Access API (no authentication required)
const getPublicBaseUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/';
  // Ensure backendUrl ends with / and remove trailing slash if present
  const baseUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
  return `${baseUrl}/public/share`;
};

const PUBLIC_BASE_URL = getPublicBaseUrl();

export const publicAccessApi = {
  // Verify public share link (link-only, no password)
  verifyShare: (shareId) =>
    axios.post(`${PUBLIC_BASE_URL}/${shareId}/verify`),

  // Get file info for a public share
  getFileInfo: (shareId) =>
    axios.get(`${PUBLIC_BASE_URL}/${shareId}/file-info`),

  // Get Forge viewer config for a public share
  getViewerConfig: (shareId) =>
    axios.get(`${PUBLIC_BASE_URL}/${shareId}/viewer-config`),

  // Download file from a public share
  downloadFile: (shareId) =>
    axios.get(`${PUBLIC_BASE_URL}/${shareId}/download`, { responseType: 'blob' }),

  // Get comments for the file (public share)
  getComments: (shareId) =>
    axios.get(`${PUBLIC_BASE_URL}/${shareId}/comments`),

  // Add a comment (public share) — optional name is shown as author
  addComment: (shareId, text, name) =>
    axios.post(`${PUBLIC_BASE_URL}/${shareId}/comments`, {
      text,
      ...(name ? { name } : {}),
    }),

  // Record client approval for the file (public share)
  // Record client approval for the file (public share) — name is required (approver, not commenter)
  approve: (shareId, name) =>
    axios.post(`${PUBLIC_BASE_URL}/${shareId}/approve`, { name }),
};

// API Documentation
export const apiDocs = {
  // Get API documentation
  get: () => fileShareApi.get('/docs'),
};

export default fileShareApi;

